import React from 'react';
import { useNavigate } from 'react-router-dom';
import useTechSession from '../../hooks/useTechSession';
import useLanguage from '../../hooks/useLanguage';
import useVoice from '../../hooks/useVoice';
import useAPI from '../../hooks/useAPI';
import TechButton from '../../components/tech/TechButton';
import VoiceButton from '../../components/tech/VoiceButton';
import { endTechDay } from '../../services/api';

/**
 * End of day summary — completed tasks, hours, prediction accuracy, voice.
 */
export default function EndDay() {
  const navigate = useNavigate();
  const { session, logout } = useTechSession();
  const { language, t, isRTL } = useLanguage();
  const { speak, speaking } = useVoice();

  const { data, loading, error } = useAPI(
    () => endTechDay(session?.technicianId, language),
    [session?.technicianId]
  );

  if (!session) { navigate('/tech'); return null; }
  if (loading) return <div style={styles.center}>{t.loading}</div>;
  if (error) return <div style={styles.center}><span style={{ color: '#d63031' }}>{error}</span></div>;

  const summary = data?.summary || {};

  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{t.endDay}</h2>
          <VoiceButton
            mode="speak"
            active={speaking}
            onClick={() => {
              if (data?.voice_summary?.text) {
                speak(data.voice_summary.text, data.voice_summary.language);
              }
            }}
          />
        </div>
        <div style={{ fontSize: 13, color: '#8b949e', marginTop: 4 }}>
          {data?.project} &bull; {data?.date}
        </div>
      </div>

      {/* Stats cards */}
      <div style={styles.statsRow}>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: '#00b894' }}>{summary.completed || 0}</div>
          <div style={styles.statLabel}>{t.completed}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{summary.total_assigned || 0}</div>
          <div style={styles.statLabel}>Total</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{summary.work_hours || 0}h</div>
          <div style={styles.statLabel}>{t.hours}</div>
        </div>
      </div>

      {/* Details */}
      <div style={styles.section}>
        <div style={styles.row}>
          <span style={styles.label}>Avg per task</span>
          <span style={styles.val}>{summary.avg_minutes_per_task || 0} {t.minutes}</span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>Escalated</span>
          <span style={styles.val}>{summary.escalated || 0}</span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>{t.remaining}</span>
          <span style={styles.val}>{summary.remaining || 0}</span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>AI Prediction Accuracy</span>
          <span style={{ ...styles.val, color: data?.prediction_accuracy > 70 ? '#00b894' : '#fdcb6e' }}>
            {data?.prediction_accuracy || 0}%
          </span>
        </div>
      </div>

      {/* Parts used */}
      {data?.parts_used?.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>{t.partsReplaced}</h3>
          <div style={styles.chipList}>
            {data.parts_used.map((p, i) => <span key={i} style={styles.chip}>{p}</span>)}
          </div>
        </div>
      )}

      {/* Escalated tasks */}
      {data?.escalated_tasks?.length > 0 && (
        <div style={styles.section}>
          <h3 style={{ ...styles.sectionTitle, color: '#e17055' }}>Escalated</h3>
          {data.escalated_tasks.map((et, i) => (
            <div key={i} style={{ fontSize: 13, color: '#b1bac4', padding: '4px 0' }}>
              {t.robot} #{et.robot} — {et.error?.replace(/_/g, ' ')}
              {et.reason && <div style={{ fontSize: 11, color: '#8b949e' }}>{et.reason}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Logout */}
      <TechButton
        fullWidth
        variant="secondary"
        onClick={() => { logout(); navigate('/tech'); }}
        style={{ marginTop: 20 }}
      >
        {t.logout}
      </TechButton>
    </div>
  );
}

const styles = {
  center: { textAlign: 'center', padding: 32, color: '#8b949e' },
  header: {
    background: '#161b22', borderRadius: 12, padding: '16px', marginBottom: 14,
  },
  statsRow: { display: 'flex', gap: 10, marginBottom: 14 },
  statCard: {
    flex: 1, background: '#161b22', borderRadius: 12,
    padding: '14px 12px', textAlign: 'center', border: '1px solid #30363d',
  },
  statValue: { fontSize: 24, fontWeight: 700, color: '#e6edf3' },
  statLabel: { fontSize: 11, color: '#8b949e', marginTop: 2 },
  section: {
    background: '#161b22', borderRadius: 12, padding: '14px 16px', marginBottom: 12,
  },
  sectionTitle: { fontSize: 14, fontWeight: 600, margin: '0 0 8px 0', color: '#e6edf3' },
  row: {
    display: 'flex', justifyContent: 'space-between', padding: '6px 0',
    borderBottom: '1px solid #30363d20',
  },
  label: { fontSize: 13, color: '#8b949e' },
  val: { fontSize: 13, color: '#e6edf3', fontWeight: 600 },
  chipList: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  chip: {
    fontSize: 12, padding: '4px 10px', background: '#30363d',
    borderRadius: 16, color: '#b1bac4',
  },
};
